import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { Message } from '@core/interfaces';

@Component({
  selector: 'z-toast-item',
  templateUrl: './toast-item.component.html',
  animations: [
    trigger('messageState', [
      state(
        'visible',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        })
      ),
      transition('void => *', [style({ transform: '{{showTransformParams}}', opacity: 0 }), animate('{{showTransitionParams}}')]),
      transition('* => void', [
        animate(
          '{{hideTransitionParams}}',
          style({
            height: 0,
            opacity: 0,
            transform: '{{hideTransformParams}}',
          })
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastItemComponent implements OnInit, AfterViewInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  @Input() index: number;
  @Input() message: Message;

  messageDefaults: Message = {
    id: null,
    key: null,
    icon: null,
    title: null,
    sticky: false,
    message: null,
    duration: 5000,
    progressBar: true,
    severity: 'info',
    styleClass: null,
    closable: true,
    closeLabel: null,
    action: undefined,
    actionLabel: null,
  };

  @Input() showTransformOptions: string;
  @Input() hideTransformOptions: string;
  @Input() showTransitionOptions: string;
  @Input() hideTransitionOptions: string;

  @Output() open: EventEmitter<any> = new EventEmitter();
  @Output() close: EventEmitter<any> = new EventEmitter();

  active: boolean = false;
  timeoutElapsed: boolean = false;
  timeout: any;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(private translocoService: TranslocoService, private zone: NgZone) {}

  ngOnInit() {
    this.message = {
      ...this.messageDefaults,
      ...this.message,
    };

    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.message.closeLabel = this.message.closeLabel ?? this.translate('core.components.toast.close_label');
      });

    if (this.message.sticky && this.message.closable === false && !this.message.action) {
      this.message.duration = 30000;
    }

    this.message.progressBar = this.message.progressBar ?? this.message.duration ? true : false;
    this.message.message = this.message.message.replace(/(\\n|\n)/g, '<br />');
  }

  ngAfterViewInit() {
    this.initTimeout();

    this.open.emit({
      index: this.index,
      message: this.message,
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.clearTimeout();
  }

  initTimeout() {
    if (!this.message.sticky) {
      this.zone.runOutsideAngular(() => {
        this.timeout = setTimeout(() => {
          this.timeoutElapsed = true;

          if (!this.active) {
            this.close.emit({
              index: this.index,
              message: this.message,
            });
          }
        }, this.message.duration || 3000);
      });
    }
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  onMouseEnter() {
    this.active = true;
  }

  onMouseLeave() {
    this.active = false;

    if (this.timeoutElapsed) {
      this.close.emit({
        index: this.index,
        message: this.message,
      });
    }
  }

  onCloseClick(event: any) {
    this.clearTimeout();

    this.close.emit({
      index: this.index,
      message: this.message,
    });

    event.preventDefault();
  }

  onActionClick(event: any) {
    this.clearTimeout();

    this.close.emit({
      index: this.index,
      message: this.message,
    });

    event.preventDefault();

    this.message.action(event);
  }
}
